"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ViewPicker = void 0;
const hooks_1 = require("../../hooks");
const react_1 = __importDefault(require("react"));
const components_1 = require("@apitable/components");
const ahooks_1 = require("ahooks");
const interface_1 = require("../../interface");
const lodash_1 = require("lodash");
const core_1 = require("../../core");
const ViewPickerWrapper = (props) => {
    const [pickerViewId, setPickerViewId, editable] = (0, hooks_1.useCloudStorage)('_picker_view_id');
    const { datasheet, viewId, onChange, controlJump } = props;
    const { installPosition } = (0, hooks_1.useMeta)();
    const viewsMeta = (0, hooks_1.useViewsMeta)();
    const syncPickerViewId = (viewId) => {
        if (editable &&
            installPosition === interface_1.InstallPosition.Dashboard &&
            !datasheet &&
            controlJump &&
            pickerViewId !== viewId &&
            viewsMeta.some(viewMeta => viewMeta.id === viewId)) {
            /**
             * Asynchronously go through the jump to the associated datasheet to set the view,
             * without affecting the current operation
             */
            setTimeout(() => {
                setPickerViewId(viewId);
            });
        }
    };
    const onChangeProxy = (option) => {
        onChange && onChange(option);
        syncPickerViewId(option.value);
    };
    (0, ahooks_1.useMount)(() => {
        syncPickerViewId(viewId);
    });
    return react_1.default.createElement(ViewPickerBase, Object.assign({}, props, { onChange: onChangeProxy }));
};
const ViewPickerBase = (props) => {
    const { viewId, onChange, placeholder, datasheet, disabled, allowedTypes } = props;
    const allViewsMeta = (0, hooks_1.useViewsMeta)(datasheet);
    const viewsMeta = allViewsMeta.filter(viewMate => !allowedTypes || (allowedTypes === null || allowedTypes === void 0 ? void 0 : allowedTypes.includes(viewMate.type)));
    return (react_1.default.createElement(components_1.Select, { placeholder: placeholder || (0, core_1.t)(core_1.Strings.please_choose), value: viewId, disabled: disabled, options: viewsMeta.map(viewMate => ({ label: viewMate.name, value: viewMate.id })), onSelected: option => onChange ? onChange(option) : lodash_1.noop }));
};
/**
 *
 * View selector, using this components you can select all views of the currently datasheet.
 *
 * @returns
 *
 * #### Example
 *
 * Common usage.
 *
 * ``` ts
 * import React, { useState } from 'react';
 * import { ViewPicker } from '@apitable/widget-sdk';
 * const Example = () => {
 *   const [viewId, setViewId] = useState()
 *   return <ViewPicker viewId={viewId} onChange={option => setViewId(option.value)} />
 * }
 *
 * ```
 *
 * Use {@link useCloudStorage} implementing persistent storage.
 *
 * ``` ts
 * import React from 'react';
 * import { ViewPicker, useCloudStorage } from '@apitable/widget-sdk';
 * const ExampleCloud = () => {
 *   const [viewId, setViewId] = useCloudStorage('selectViewId');
 *   return <ViewPicker viewId={viewId} onChange={option => setViewId(option.value)} />;
 * };
 * ```
 *
 */
exports.ViewPicker = ViewPickerWrapper;
